import { UnauthorizedError } from "@ember-data/adapter/error";
import JSONAPIAdapter from "@ember-data/adapter/json-api";
import type ApplicationInstance from "@ember/application/instance";
import { getOwner } from "@ember/owner";
import type RouterService from "@ember/routing/router-service";
import { service } from "@ember/service";
import type SemeiaSessionService from "parog-web/services/session";

export default class CoreAdapter extends JSONAPIAdapter {
  @service declare router: RouterService;
  @service declare session: SemeiaSessionService;

  override get headers(): HeadersInit {
    const headers = {};

    // during the login steps the session is not authenticated yet, but the `access_token` property of the session is set
    // for all other requests the session will be authenticated
    if (this.session.accessToken) {
      headers["Authorization"] = `Bearer ${this.session.accessToken}`;

      let authenticated_as;
      if (this.session.patient) {
        authenticated_as = `Patient-${this.session.patient.id}/${this.session.patient.uuid}`;
      } else if (this.session.medic) {
        authenticated_as = `Medic-${this.session.medic.id}/${this.session.medic.uuid}`;
      }

      if (authenticated_as) {
        headers["X-Authenticated-As"] = authenticated_as;
      }
    }

    headers["X-Frontend-Version"] = (
      (getOwner(this) as ApplicationInstance).resolveRegistration(
        "config:environment"
      ) as Environment
    ).APP.version;

    return headers;
  }

  override get namespace() {
    return "api/v1";
  }

  override handleResponse(status, headers, payload, requestData) {
    const result = super.handleResponse(status, headers, payload, requestData);

    if (result instanceof UnauthorizedError) {
      if (this.session.medic) {
        this.router.replaceWith("medics-web.logout");
      }

      if (this.session.patient) {
        this.router.replaceWith("patient-web.logout");
      }
    }

    return result;
  }

  override pathForType(modelName) {
    // Configuration models are accessible through the /api/v1/configuration route.
    if (modelName.startsWith("configuration")) {
      return "configurations";
    }

    return super.pathForType(modelName);
  }
}
